import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  DeleteModal,
  PLATFORM_THEME_COLORS,
  Separator,
  Typography,
} from '@thinknet-gmbh/platform-common-ui';

import { IDocument, IEmployee, IJob } from 'src/pages/jobs/jobs.interfaces';
import { SetJobToEmployeeModal } from 'src/pages/jobs/components/set-job-to-employee-modal';
import { useMakeMemberWorkingConditionsInactive } from 'src/hooks/employees/make-member-working-conditions-inactive.hook';

import { JobAdDescription } from './components/job-ad-description';
import { JobDocumentsList } from './components/job-documents-list';
import { JobProfileInfo } from './components/job-profile-info';
import { JOB_DETAIL_VIEW_MODALS } from './components/job-profile-info/job-profile-info.constants';
import { JobDetailsWrapper } from './job-details-view.styles';

export type JobDetailsViewModal = {
  modal: string;
  data: { job: IJob; jobLocationId: string; jobSupervisorId: string; employee: IEmployee | null };
} | null;

const JobDetailsView = ({ job }: { job: IJob }) => {
  const { t } = useTranslation(['jobs', 'common']);
  const [activeModal, setActiveModal] = useState<JobDetailsViewModal>(null);

  const makeMemberWorkingConditionsInactive = useMakeMemberWorkingConditionsInactive({
    onSuccess: () => setActiveModal(null),
  });

  const onDeleteSubmit = () => {
    makeMemberWorkingConditionsInactive({
      variables: { memberId: activeModal?.data.employee?._id },
    });
  };

  return (
    <JobDetailsWrapper>
      {activeModal && (
        <>
          <SetJobToEmployeeModal
            isOpened={activeModal.modal === JOB_DETAIL_VIEW_MODALS.setJobToEmployee}
            onClose={() => setActiveModal(null)}
            data={activeModal.data}
          />
          <DeleteModal
            translate={t}
            isOpened={activeModal.modal === JOB_DETAIL_VIEW_MODALS.removeFromJob}
            onClose={() => setActiveModal(null)}
            submitBtnText={t('common:buttons.delete')}
            title={t('jobs:deleteEmployee.title')}
            subTitle={t('jobs:deleteEmployee.subtitle', {
              employee: activeModal.data.employee?.profile.displayName,
            })}
            onSubmit={onDeleteSubmit}
          />
        </>
      )}
      <JobProfileInfo
        job={job}
        handleModals={(modalData: JobDetailsViewModal) => setActiveModal(modalData)}
      />
      <Separator />
      <Typography weight="bold" fontColor={PLATFORM_THEME_COLORS.grayVariations.dark}>
        {t('jobs:labels.document')}
      </Typography>
      <div>
        {job.documents.map((document: IDocument) => (
          <JobDocumentsList key={document._id} document={document} />
        ))}
      </div>
      <Separator />
      <Typography weight="bold" fontColor={PLATFORM_THEME_COLORS.grayVariations.dark}>
        {t('jobs:labels.jobAdvertisementDescription')}
      </Typography>
      <JobAdDescription
        advertisementDescription={job.advertisementDescription}
        neededSkills={job.neededSkills}
      />
    </JobDetailsWrapper>
  );
};

export default JobDetailsView;
